import {
  addFolderMember,
  deleteFolderMember,
  listFolderMembers,
  updateFolderAccess,
  updateFolderMember,
} from '@/backend/folder'
import type {
  AssignableRole,
  ResourceAccessType,
  ResourceMember,
} from '@/modules/IdentityAndAccess/types'
import {
  serializeWorkspaceMember,
  useWorkspaceMembers,
  type WorkspaceMember,
} from '@/modules/WorkspaceSettings/useWorkspaceMembers'
import { invariant } from '@/shared/utils/typeAssertions'
import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 *
 */
export const useFolderMembersStore = defineStore('folderMembers', () => {
  const workspaceMemberStore = useWorkspaceMembers()

  // the effective permission for anyone_in_workspace
  const defaultAccessType = ref<ResourceAccessType | null>(null)

  /**
   * Users who have explicit permissions to access this folder, i.e. they are not
   * inheriting permission from a parent folder or the workspace.
   */
  const folderMembers = ref<WorkspaceMember[]>([])

  const loadFolderMembers = async ({
    folderId,
    workspaceId,
  }: {
    folderId: string
    workspaceId: string
  }) => {
    folderMembers.value = []
    const res = await listFolderMembers({
      folderId,
      workspaceId,
    })

    if (res.ok) {
      defaultAccessType.value = res.data.access_type
      folderMembers.value = res.data.data.reduce<WorkspaceMember[]>((acc, member) => {
        if (typeof member.user === 'string') {
          // e.g. 'anyone_in_folder', 'anyone'
          return acc
        }

        return [...acc, serializeWorkspaceMember(member)]
      }, [])
    }
  }

  /** Add a user to the folder with an explicit permission */
  const addMember = async ({
    role,
    userId,
    workspaceId,
    folderId,
  }: {
    userId: string
    role: AssignableRole
    workspaceId: string
    folderId: string
  }) => {
    const workspaceMember = workspaceMemberStore.getMember(userId)
    invariant(workspaceMember, 'User is not a member of this workspace')

    const optimisticMember: ResourceMember = {
      ...workspaceMember,
      role,
    }
    folderMembers.value.push(optimisticMember)

    const res = await addFolderMember({
      workspaceId,
      folderId,
      userId,
      role,
    })

    if (!res.ok) {
      folderMembers.value = folderMembers.value.filter((member) => member.id !== userId)
    }

    return res
  }

  /**  Update the permission for a user on this folder */
  const updateMemberRole = async ({
    role,
    userId,
    workspaceId,
    folderId,
  }: {
    userId: string
    role: AssignableRole
    workspaceId: string
    folderId: string
  }) => {
    const existingMember = folderMembers.value.find((member) => member.id === userId)
    invariant(existingMember, 'User is not a member of this folder')

    const oldRole = existingMember.role
    existingMember.role = role

    const res = await updateFolderMember({
      workspaceId,
      folderId,
      userId,
      role,
    })

    if (!res.ok) {
      existingMember.role = oldRole
    }

    return res
  }

  /**
   * Remove a user's explicit permission on this folder, giving them the default
   * access type.
   */
  const deleteMember = async ({
    userId,
    workspaceId,
    folderId,
  }: {
    userId: string
    workspaceId: string
    folderId: string
  }) => {
    const existingMemberIndex = folderMembers.value.findIndex((member) => member.id === userId)
    const existingMember = folderMembers.value[existingMemberIndex]
    invariant(existingMember, 'User is not a member of this folder')

    folderMembers.value = folderMembers.value.filter((member) => member.id !== userId)

    const res = await deleteFolderMember({
      workspaceId,
      folderId,
      userId,
    })

    if (!res.ok) {
      folderMembers.value.splice(existingMemberIndex, 0, existingMember)
    }

    return res
  }

  /** Set the access type given to users with no explicit permissions */
  const setDefaultAccess = async ({
    accessType,
    workspaceId,
    folderId,
  }: {
    accessType: ResourceAccessType
    workspaceId: string
    folderId: string
  }) => {
    const oldAccessType = defaultAccessType.value
    defaultAccessType.value = accessType

    const res = await updateFolderAccess({
      workspaceId,
      folderId,
      accessType,
    })

    if (!res.ok) {
      defaultAccessType.value = oldAccessType
    }

    return res
  }

  const $reset = () => {
    defaultAccessType.value = null
    folderMembers.value = []
  }

  return {
    defaultAccessType,
    setDefaultAccess,

    folderMembers,
    loadFolderMembers,

    addMember,
    updateMemberRole,
    deleteMember,

    $reset,
  }
})
